import { toPairs } from 'lodash';
import moment from 'moment';
import { Squad } from 'api/models';
import {
  AssigneeValue,
  TeamId,
  UserId,
} from 'components/organisms/tasks/fields';
import { Task as NormalizedTask } from 'data/tasks/schema';
import { TaskField } from 'stores/fields';
import { Task as StoreTask, Task } from 'stores/tasks';

export type TaskLookupItem = Pick<Task, 'taskName' | 'taskId' | 'parentTask'>;

export const TaskLookupItem = ({
  taskName,
  taskId,
  parentTask,
}: TaskLookupItem): TaskLookupItem => ({
  taskName,
  taskId,
  parentTask,
});

const getTaskTree = (
  lookup: Record<number, TaskLookupItem>,
  task: TaskLookupItem,
): TaskLookupItem[] => {
  return task.parentTask == null || lookup[task.parentTask] == null
    ? [TaskLookupItem(task)]
    : getTaskTree(lookup, TaskLookupItem(lookup[task.parentTask])).concat(
        TaskLookupItem(task),
      );
};

export const toAssigneeValues = (
  squad: Squad = { users: [], teams: [] },
): AssigneeValue[] =>
  squad.users
    .map(({ userId }) => UserId(userId) as AssigneeValue)
    .concat(squad.teams.map(({ teamId }) => TeamId(teamId)));

export const toStoreTask =
  (
    field_lookup: Record<number, TaskField>,
    tasks_lookup: Record<number, TaskLookupItem>,
  ) =>
  (normalized_task: NormalizedTask): StoreTask => {
    const {
      taskId,
      parentTask,
      project,
      responsible,
      fields: task_fields,
      subTaskOrder,
      linkedResources,
      ...task
    } = normalized_task;

    const fields = {};

    toPairs(field_lookup).forEach(([fieldId, field]) => {
      if (!field.customField) {
        fields[field.fieldName] =
          task[field.fieldName] ?? (field.metaData as any)?.default;
      } else {
        fields[field.fieldName] =
          task_fields.find((x) => x.fieldId.toString() === fieldId)
            ?.fieldValue ?? (field.metaData as any)?.default;
      }
    });

    // These keys will not be stored ["commentThreadId", "projectPhase", "assigneeAuditMeta", "attachments", "subTasks", "parentTaskObject", "approvals"]
    return {
      ...fields,
      taskId,
      private: task.private,
      responsible: (responsible ?? [])
        .filter(Boolean)
        .map(({ companyId }) => companyId),
      parentTask,
      project,
      taskTree: getTaskTree(tasks_lookup, normalized_task as any),
      phaseName: task.projectPhase?.projectPhaseName,
      projectPhaseId: task.projectPhase?.projectPhaseName,
      phaseId: task.projectPhase?.projectPhaseId,
      assignee: toAssigneeValues(task.assignee),
      subTaskOrder,
      jiraIntegration: linkedResources?.find(
        (lr) => lr.externalIntegrationType === 'jira',
      )?.context?.jiraIssueUrl,
      completedLateBy:
        task.completedAt &&
        task.dueDate &&
        moment(task.completedAt).diff(moment(task.dueDate), 'days'),
      formInstanceId: task.formInstanceId,
      dependentTasks: task.dependentTasks,
      fieldsAccessMetaInBase3: task?.fieldsAccessMetaInBase3,
    } as unknown as StoreTask;
  };
